import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import {
  MapPin, ChevronRight, Loader2, ClipboardPlus, X, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const SPINE = { critical: 'var(--critical)', high: 'var(--overdue)', medium: 'var(--overdue)', low: 'var(--border)' };
const DOT = { reported: '#8A857A', open: '#C23A00', critical: '#A81616', note: '#8A857A', pass: '#0E6B3F', work_order: '#8A6D00' };
const SEV_CHIP = {
  critical: 'bg-[var(--critical-bg)] text-[var(--critical-ink)] border-[var(--critical-edge)]',
  high: 'bg-[var(--overdue-bg)] text-[var(--overdue-ink)] border-[var(--overdue-edge)]',
  medium: 'bg-[var(--overdue-bg)] text-[var(--overdue-ink)] border-[var(--overdue-edge)]',
  low: 'bg-[var(--neutral-bg)] text-[var(--ink-secondary)] border-[var(--border-strong)]',
};

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const MobileDefectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();

  const [loading, setLoading] = useState(true);
  const [defect, setDefect] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState('');
  const [confirmResolve, setConfirmResolve] = useState(false);

  const load = async () => {
    try { const r = await mobileApi.getDefect(id, getToken()); setDefect(r.data); }
    catch { toast.error('Failed to load defect'); navigate('/mobile/defects'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const raiseWO = async () => {
    setBusy(true);
    try { const r = await mobileApi.raiseDefectWorkOrder(id, getToken()); toast.success(`${r.data.reference} raised & assigned to you`); navigate(`/mobile/work-orders/${r.data.work_order_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to raise work order'); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await mobileApi.addDefectNote(id, note.trim(), getToken()); setNote(''); setShowNote(false); await load(); toast.success('Note added'); }
    catch { toast.error('Failed to add note'); }
    finally { setBusy(false); }
  };
  const resolve = async () => {
    setBusy(true);
    try { await mobileApi.resolveDefect(id, getToken()); setConfirmResolve(false); await load(); toast.success('Defect marked resolved'); }
    catch { toast.error('Failed to resolve'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>;
  if (!defect) return null;

  const sev = (defect.severity || 'medium').toLowerCase();
  const assetId = defect.asset_unit_number || defect.asset_name || 'No asset on record';
  const assetLine = [[defect.asset_make, defect.asset_model].filter(Boolean).join(' '), defect.asset_year, defect.asset_location].filter(Boolean).join(' · ');
  const age = daysSince(defect.created_at);
  const photos = defect.photos || [];
  const events = (defect.events || []).slice().sort((a, b) => new Date(a.at) - new Date(b.at));
  const resolved = defect.status === 'resolved';
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-44" data-testid="defect-detail">
      <MobileHeader showBack
        title={<span className="flex flex-col leading-tight">
          <span className="font-mono text-[10.5px] font-normal text-[var(--ink-tertiary)]">{defect.defect_number}</span>
          <span className="text-[15.5px] font-bold text-[var(--ink-primary)] truncate">{defect.title}</span>
        </span>} />

      <div className="px-4 py-4 space-y-4">
        {/* summary */}
        <MobileCard spine={SPINE[sev]} padding="normal">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold border ${SEV_CHIP[sev]}`}>{cap(sev)}</span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold border bg-[var(--surface-sunken)] text-[var(--ink-secondary)] border-[var(--border)]">{cap(defect.status)}</span>
            </div>
            {age != null && <span className="text-[13px] font-semibold shrink-0" style={{ color: sev === 'critical' ? 'var(--critical-ink)' : 'var(--overdue-ink)' }}>{age} {age === 1 ? 'day' : 'days'} open</span>}
          </div>
          <div className="text-[15.5px] font-bold text-[var(--ink-primary)] mt-2 leading-snug">{defect.title}</div>
          {defect.description && <div className="text-[13.5px] text-[var(--ink-secondary)] mt-1 leading-snug">{defect.description}</div>}
        </MobileCard>

        {/* asset */}
        <MobileCard padding="normal" onClick={defect.asset_id ? () => navigate(`/mobile/assets/${defect.asset_id}`) : undefined}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-mono text-[14px] font-bold text-[var(--ink-primary)]">{assetId}</div>
              {assetLine && <div className="text-[12.5px] text-[var(--ink-secondary)] mt-0.5 truncate">{assetLine}</div>}
            </div>
            {defect.asset_id && <ChevronRight className="w-5 h-5 text-[var(--ink-tertiary)] shrink-0" />}
          </div>
          {defect.asset_has_device && defect.asset_id && (
            <button onClick={(e) => { e.stopPropagation(); navigate(`/mobile/assets/${defect.asset_id}`); }} className="w-full flex items-center gap-2 mt-3 pt-3 border-t border-[var(--divider)] text-[13px] font-semibold text-[var(--volt-ink)]">
              <MapPin className="w-4 h-4" /> Locate asset
            </button>
          )}
        </MobileCard>

        {/* photos */}
        {photos.length > 0 && (
          <div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((p, i) => (
                <img key={i} src={p} alt="" className="rounded-[12px] object-cover shrink-0" style={{ width: 76, height: 76, border: '1px solid var(--border)' }} />
              ))}
            </div>
            <p className="text-[12.5px] text-[var(--ink-tertiary)] mt-1">{photos.length} photo{photos.length === 1 ? '' : 's'} from the report</p>
          </div>
        )}

        {/* activity */}
        <MobileCard padding="none">
          <div className="px-4 bg-[var(--surface-sunken)] rounded-t-[13px] flex items-center" style={{ height: 42 }}>
            <span className="text-[11px] font-bold uppercase tracking-[0.11em] text-[var(--ink-tertiary)]">Activity</span>
          </div>
          <div className="px-4">
            {events.map((ev, i) => (
              <div key={i} className={`flex items-start gap-3 py-3 ${i ? 'border-t border-[var(--divider)]' : ''}`}>
                <span className="shrink-0 rounded-full mt-1" style={{ width: 7, height: 7, background: DOT[ev.status] || '#8A857A' }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-[var(--ink-primary)]">{ev.text}</div>
                  <div className="font-mono text-[10.5px] text-[var(--ink-tertiary)] mt-0.5">{fmt(ev.at)}</div>
                </div>
              </div>
            ))}
          </div>
        </MobileCard>

        {/* inline note editor */}
        {showNote && (
          <MobileCard padding="normal">
            <textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note to this defect"
              data-testid="defect-note-input"
              className="w-full min-h-[72px] rounded-[10px] bg-white border border-[var(--border-strong)] px-3 py-2 text-[16px] text-[var(--ink-primary)] outline-none focus:border-[#1A1A1A] focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" />
            <div className="flex gap-2 mt-2">
              <button onClick={addNote} disabled={busy || !note.trim()} data-testid="defect-note-save" className="h-10 px-4 rounded-[10px] bg-[var(--volt)] text-black text-[13px] font-bold disabled:opacity-40" style={{ border: '1px solid var(--volt-edge)' }}>Save note</button>
              <button onClick={() => { setShowNote(false); setNote(''); }} className="h-10 px-4 rounded-[10px] text-[13px] font-semibold text-[var(--ink-secondary)]" style={{ border: '1px solid var(--border-strong)' }}>Cancel</button>
            </div>
          </MobileCard>
        )}
      </div>

      {/* bottom bar */}
      {!resolved && (
        <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pt-2.5 pb-4 bg-[var(--surface-page)]" style={{ boxShadow: 'var(--elev-floating)' }}>
          <p className="text-[12.5px] text-[var(--ink-secondary)] leading-snug mb-2.5" data-testid="defect-consequence">
            Creates a work order against <b className="text-[var(--ink-primary)]">{assetId}</b> and assigns it to you.
            {offline && <> <b className="text-[var(--ink-primary)]">You are offline</b> — saved here and sent when signal returns.</>}
          </p>
          <button onClick={raiseWO} disabled={busy || !defect.asset_id} data-testid="defect-raise-wo"
            className="w-full rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.99] transition-transform"
            style={{ height: 56, fontWeight: 800, border: '1px solid var(--volt-edge)' }}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ClipboardPlus className="w-5 h-5" /> RAISE WORK ORDER</>}
          </button>
          <div className="flex items-center justify-center gap-6 mt-1">
            <button onClick={() => setShowNote(true)} data-testid="defect-add-note" className="h-11 text-[13px] font-semibold text-[var(--volt-ink)]">Add a note</button>
            <button onClick={() => setConfirmResolve(true)} data-testid="defect-mark-resolved" className="h-11 text-[13px] font-semibold text-[var(--volt-ink)]">Mark resolved</button>
          </div>
        </div>
      )}

      {resolved && (
        <div className="fixed bottom-0 left-0 right-0 z-40 px-4 py-4 bg-[var(--surface-page)] flex items-center justify-center gap-2" style={{ boxShadow: 'var(--elev-floating)' }}>
          <CheckCircle2 className="w-5 h-5 text-[var(--pass-ink)]" />
          <span className="text-[13.5px] font-semibold text-[var(--pass-ink)]">Resolved{defect.resolved_by_name ? ` by ${defect.resolved_by_name}` : ''}</span>
        </div>
      )}

      {/* resolve confirm */}
      {confirmResolve && (
        <div className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(26,26,26,0.45)' }} onClick={() => setConfirmResolve(false)}>
          <div className="w-full bg-[var(--surface-card)] rounded-t-[18px] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[17px] font-bold text-[var(--ink-primary)]">Mark this defect resolved?</h3>
              <button onClick={() => setConfirmResolve(false)}><X className="w-5 h-5 text-[var(--ink-tertiary)]" /></button>
            </div>
            <p className="text-[13.5px] text-[var(--ink-secondary)] leading-snug">
              This closes the safety record on <b className="text-[var(--ink-primary)]">{assetId}</b>{sev === 'critical' ? ' and returns it to service' : ''}. Any linked work order stays open and must be closed separately. This is logged against your name.
            </p>
            <button onClick={resolve} disabled={busy} data-testid="defect-resolve-confirm"
              className="w-full mt-4 rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center disabled:opacity-40"
              style={{ height: 54, fontWeight: 800, border: '1px solid var(--volt-edge)' }}>
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm resolved'}
            </button>
            <button onClick={() => setConfirmResolve(false)} className="w-full h-11 mt-1 text-[13px] font-semibold text-[var(--ink-secondary)]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileDefectDetail;
